import React from 'react';
// import Bug from '../../../../../../../../../../icons/IssuesIcon.svg'; 
// import Exclamation from '../../../../../../../../../../icons/IcoExclamationCircleHollow.svg';
// import Ongoing from '../../../../../../../../../../icons/ProgressIcon.svg';
import { AddHHMMSS, formatDuration } from 'common/helpers/v1';
import { ActualChangeOverDetail, AsepticChangeoverType } from 'types/machine-health/aseptic';
import { Alarm } from 'types/machine-health/alarms';
import { CleaningStepWithKPI } from 'types/protein';
import _ from 'lodash';
import BlueArrow from 'icons/indexed-icons/blueArrow';
import ChevronDownTwo from 'icons/indexed-icons/ChevronDownTwoAlt';
import GreenCheckCircle from 'icons/indexed-icons/GreenCheckCircle';
import IssuesIcon from 'icons/indexed-icons/IssuesIcon';
import ProgressIcon from 'icons/indexed-icons/ProgressIcon';
import Chevron from 'icons/indexed-icons/chevron';
import { IcoExclamationCircleHollow } from 'icons/indexed-icons/IcoExclamationCircleHollow';

export const generateIcon = (type: string): JSX.Element => {
  switch (type) {
    case 'green checkmark':
      return <GreenCheckCircle />;
    case 'chevron right':
      return <Chevron />;
    case 'chevron down':
      return <ChevronDownTwo />;
    case 'bug':
      return <IssuesIcon />;
    case 'blue arrow':
      return <BlueArrow />;
    case 'in progress':
      return <IcoExclamationCircleHollow />;
    case 'ongoing':
      return <ProgressIcon />;
    default:
      return <></>;
  }
};

const getTargetDuration = (values, key) => {
  return (
    values.reduce((acc, item) => {
      if (item.modeDescr !== key) return acc;
      return acc + item.duration;
    }, 0) * 1000
  ); //we need to return it in milliseconds
};

const differenceBetweenActualAndTarget = (values, key, targetDetails) => {
  const actualDuration = getDurationMilliseconds(values);
  const targetDuration = getTargetDuration(targetDetails, key);
  return actualDuration - targetDuration;
};

const mergeAllAlarmsData = (subSteps: ActualChangeOverDetail[]) => {
  const allAlarms: Alarm[] = [];
  subSteps?.forEach((value) => {
    const alarms = value.alarms.map((alarm) => ({
      group: value.modeDescr,
      ...alarm
    }));
    allAlarms.push(...alarms);
  });
  return allAlarms;
};

// Return formatted duration string from a step
const getStepDuration = (subSteps: ActualChangeOverDetail[]) => {
  if (subSteps) {
    let sumDuration = 0;
    subSteps.forEach((subStep) => {
      sumDuration += new Date(subStep.endTime).getTime() - new Date(subStep.startTime).getTime();
    });
    return formatDuration(sumDuration, 'hours:mins:secs');
  }
};

const getDurationMilliseconds = (subSteps?: ActualChangeOverDetail[]) => {
  let sumDuration = 0;
  if (subSteps) {
    subSteps.forEach((subStep) => {
      sumDuration += new Date(subStep.endTime).getTime() - new Date(subStep.startTime).getTime();
    });
  }
  return sumDuration;
};

export const formatDurationString = (milliseconds: number): string => {
  return formatDuration(milliseconds, 'hours:mins:secs');
};

const getSumTargetDuration = (key: string, targetDetails: ActualChangeOverDetail[] | undefined) => {
  return targetDetails?.reduce((acc, Titem) => {
    if (Titem.modeDescr === key) {
      return acc + Titem.duration;
    }
    return acc;
  }, 0);
};

const formatAveDurationMinutes = (aveDur: number): string => {
  const hours = Math.floor(aveDur / 60);
  const minutes = aveDur % 60;

  const hoursDisplay = hours.toString().length === 1 ? `0${hours}` : hours;
  const minutesDisplay = minutes.toString().length === 1 ? `0${minutes}` : minutes;

  return `${hoursDisplay}:${minutesDisplay}:00`;
};

const getSubStepSumTargetDuration = (
  subStep: ActualChangeOverDetail,
  targetDetails: ActualChangeOverDetail[] | undefined
) => {
  let sumTargetDuration = '00:00:00';

  if (targetDetails) {
    const subSteps = targetDetails.filter(
      (targetDetail) => targetDetail.recipeDescr === subStep.recipeDescr
    );
    subSteps.forEach((subStep) => {
      sumTargetDuration =
        sumTargetDuration && subStep?.duration
          ? AddHHMMSS(sumTargetDuration, subStep?.duration)
          : '-';
    });
  }

  return sumTargetDuration;
};


const getStatus = (
  active: boolean,
  actualDetails: AsepticChangeoverType['actualDetails'],
  runLoginId: string
) => {
  if (!active) return 'Completed';
  const latestRunLoginId = actualDetails?.[actualDetails.length - 1]?.runLoginId;
  return runLoginId === latestRunLoginId ? 'in progress' : 'completed';
};

export const toCleaningStepWithKPI = (
  selectedChangeover?: AsepticChangeoverType
): CleaningStepWithKPI[] => {
  // Group tags by their 'tagGroupId'
  const active = selectedChangeover?.active ?? false;
  const groups = _(selectedChangeover?.actualDetails)
    .groupBy((actualDetail) => actualDetail.modeDescr)
    .map((values, key) => {
      // Check if any sub-step is active
    const isGroupActive = values.some((step) => step.active);
    return {
      id: values[0].modeNumber,
      group: values[0].modeDescr,
      name: key,
      active: isGroupActive,
      startTime: values[0].startTime,
      endTime: values[values.length - 1].endTime,
      averageDuration: formatAveDurationMinutes(values[0].avgDuration as unknown as number),
      alarms: mergeAllAlarmsData(values),
      duration: formatDurationString(getDurationMilliseconds(values)),
      targetDuration: formatDurationString(
        getSumTargetDuration(key, selectedChangeover?.targetDetails) * 1000
      ),
      // targetDuration: getSumTargetDuration(values, selectedChangeover?.targetDetails),
      actualVStargetDiff: differenceBetweenActualAndTarget(
        values,
        key,
        selectedChangeover?.targetDetails
      ),

      // Second level grouping by 'recipeDescr'
      subRows: _(values)
        .groupBy((subStep) => subStep.recipeDescr)
        .map((subSteps) => {
            // Check if any runLoginId is active
        const isSubGroupActive = subSteps.some((step) => step.active);
        return {
          id: subSteps[0].recipeDescr,
          name: subSteps[0].recipeDescr,
          parentName: subSteps[0].modeDescr,
          status: isSubGroupActive ? 'In Progress' : 'Completed',
          active: isSubGroupActive,
          startTime: subSteps[0].startTime + '.275000+00:00',
          endTime: subSteps[subSteps.length - 1].endTime,
          alarms: mergeAllAlarmsData(subSteps),
          duration: getStepDuration(subSteps),
          averageDuration: formatAveDurationMinutes(subSteps[0].avgDuration as unknown as number),
          targetDuration: getSubStepSumTargetDuration(
            subSteps[0],
            selectedChangeover?.targetDetails
          ),
        // Third level grouping by 'runLoginId'
          subRows:[{ newTable: _(subSteps)
          .groupBy((step) => step.runLoginId)
          .map((steps, runLoginId) => {
            return {
              id: runLoginId,
              name: runLoginId,
              mainName: subSteps[0].modeDescr,
              parentName: subSteps[0].recipeDescr,
              startTime: steps[0].startTime + '.275000+00:00',
              processLog:1,
              active: steps[0].active,
              endTime: steps[steps.length - 1].endTime,
              alarms: mergeAllAlarmsData(steps),
              duration: getStepDuration(steps),
              averageDuration: formatAveDurationMinutes(steps[0].avgDuration as unknown as number),
              targetDuration: getSubStepSumTargetDuration(steps[0], selectedChangeover?.targetDetails),
              status: getStatus(active, selectedChangeover?.actualDetails, runLoginId),
              sterilizationResult: steps[0].sterilizationResult,
            };
              })
              .value(),
            type: 'singleSessionStep',
          },
            ],
          };
        })
        .value(),
    };
  })
  .value();
  const returnData: CleaningStepWithKPI[] = [];

  for (const group of groups) {
    returnData.push(group as unknown as CleaningStepWithKPI);
  }
  return returnData;
};
